import React from 'react';
import ContentfulRow from 'components/Contentful/ContentfulRow';
import BackgroundImage from 'components/Core/BackgroundImage';
import getAlignment from 'utils/getAlignment';
import getJustify from 'components/Contentful/ContentfulRow/utils/getJustify';
import { getColor, useIsColorDark, useIsImageDark } from 'utils/colorUtility';
import ComponentMap from './ComponentMap';
import { getColumnItemProps } from './ComponentMap/utils/getColumnItemProps';
import { InnerContainer, StyledColumn } from './styles';
import HyperLink from '../Core/HyperLink';
import { flattenEntries } from '../Contentful/ContentfulLayout/utils/entriesHelpers';

const Column = ({
  alignmentDesktop,
  alignmentMobile,
  verticalAlignmentDesktop,
  verticalAlignmentMobile,
  colLength,
  items,
  styles,
  templateName,
  widthMobile,
  widthDesktop,
  parentBackgroundColor,
  orderMobile,
  orderDesktop,
  parentAlignDesktop,
  horizontalContentFlowDesktop,
  horizontalContentFlowMobile,
  useCtaRouting,
  useCtaParentRouting,
}) => {
  const hashStyles = {};

  if (styles) {
    Object.keys(styles).forEach((key) => {
      hashStyles[`$${key}`] = styles[key];
    });
  }

  const { $backgroundColor, $borderColor, $marginMobile, $marginDesktop } = hashStyles || {};
  const backgroundImageUrl = styles?.backgroundImage?.image?.file?.url;
  const bgColor = getColor($backgroundColor || parentBackgroundColor);
  const defaultWidth = 24 / colLength;
  const alignMobile = getJustify(alignmentMobile);
  const alignDesktop = getJustify(alignmentDesktop);
  const verticalAlignMobile = getAlignment(verticalAlignmentMobile);
  const verticalAlignDesktop = getAlignment(verticalAlignmentDesktop);
  const borderColorVar = getColor($borderColor);
  const imageDark = useIsImageDark(backgroundImageUrl);
  const colorDark = useIsColorDark(bgColor);

  const routingUrl = useCtaRouting
    ? flattenEntries(items).find((x) => x.__typename === 'ContentfulCallToAction').url
    : null;

  const InnerContainerComp = (
    <InnerContainer
      {...hashStyles}
      id={`${templateName ? templateName.split(' ').join('-').toLowerCase() : ''}`}
      $backgroundColor={bgColor}
      $horizontalContentFlowDesktop={horizontalContentFlowDesktop}
      $horizontalContentFlowMobile={horizontalContentFlowMobile}
      $borderColor={borderColorVar}
      $justifyMobile={alignMobile}
      $justifyDesktop={alignDesktop}
      $alignMobile={verticalAlignMobile.flexAlignment}
      $alignDesktop={verticalAlignDesktop.flexAlignment}
      className={`column-inner-container ${
        templateName?.toLowerCase().includes('eventcard') ? 'h-full' : ''
      }`}
    >
      {items?.map((item, index) => {
        if (item.__typename === 'ContentfulRow') {
          return (
            <ContentfulRow
              key={index}
              {...item}
              parentBackgroundColor={$backgroundColor || parentBackgroundColor}
              childRow
              parentAlignDesktop={parentAlignDesktop}
              useCtaParentRouting={useCtaRouting}
            />
          );
        }

        if (item.__typename === 'ContentfulComponentCollection') {
          item = item.components[0];
        }

        const Component = ComponentMap[item.__typename];
        const customProps = getColumnItemProps(
          item,
          item.__typename,
          bgColor,
          alignDesktop,
          alignMobile,
          colorDark,
          imageDark,
          index,
          $marginDesktop,
          $marginMobile,
          parentBackgroundColor,
          useCtaParentRouting,
        );

        const componentProps = {
          ...item,
          ...customProps,
        };

        return Component ? <Component {...componentProps} key={index} /> : null;
      })}
    </InnerContainer>
  );

  return (
    <StyledColumn
      {...hashStyles}
      $orderDesktop={orderDesktop}
      $orderMobile={orderMobile}
      xs={widthMobile || 24}
      sm={widthDesktop || widthMobile || defaultWidth}
      className={`column-root ${
        templateName ? templateName.split(' ').join('-').toLowerCase() : ''
      }`}
      $customCss={styles?.customStyling?.customStyling}
    >
      <BackgroundImage image={styles?.backgroundImage} />
      {useCtaRouting && routingUrl ? (
        <HyperLink href={routingUrl}>{InnerContainerComp}</HyperLink>
      ) : (
        InnerContainerComp
      )}
    </StyledColumn>
  );
};

export default Column;
